import { closingStockQtyForProduct } from '../redux/closingStockApi';
import {
  average,
  calcAvgUptFromWeeks,
  calcBuildTo,
  calcFinalOrder,
  calcGcBuffer,
  calcProjectedGc,
  calcSpsOrders,
  calcSuggestedOrder,
  roundTo,
  toNumber,
} from './billToOrderCalc';
import { itemPlanningValue, productSpsValue } from './billToItemCatalog';
import {
  metricsForCatalogItem,
  projectedForCatalogItem,
} from './billToDailySales';

export function itemSkuKey(item) {
  return item?.selectionKey || `${item?.sheet || ''}::${item?.itemCode || item?.id || ''}`;
}

function firstFinite(...values) {
  for (const value of values) {
    if (value == null || value === '') continue;
    const n = Number(value);
    if (Number.isFinite(n) && n !== 0) return n;
  }
  return null;
}

export function finalOrderByOutletValue(item, wizardData, calculatedFinalOrder) {
  const rec = (wizardData?.planningBySku || {})[itemSkuKey(item)] || {};
  if (rec.finalOrderByOutletEdited) {
    return rec.finalOrderByOutlet == null ? '' : String(rec.finalOrderByOutlet);
  }
  if (calculatedFinalOrder == null || calculatedFinalOrder === '') return '';
  return String(calculatedFinalOrder);
}

export function closingStockRowsFromWizard(wizardData) {
  return Array.isArray(wizardData?.closingStockDraft?.rows)
    ? wizardData.closingStockDraft.rows
    : [];
}

export function buildSkuPlanningRows({
  items,
  wizardData,
  weeklyHistory,
  weekCount,
  closingStockRows,
}) {
  return (items || []).map((item) => {
    const skuRec = (wizardData?.planningBySku || {})[itemSkuKey(item)] || {};
    const growth = toNumber(itemPlanningValue(item, wizardData, 'projectedGrowth'));
    const buffer = toNumber(itemPlanningValue(item, wizardData, 'bufferPercent'));
    const projected = projectedForCatalogItem(item, weeklyHistory, weekCount, growth);
    const metrics = metricsForCatalogItem(weeklyHistory, item, weekCount);
    const closingStockRaw = closingStockQtyForProduct(
      closingStockRows,
      item.productId || item.id,
      item.itemCode
    );
    const closingStockQty = closingStockRaw === '' ? 0 : toNumber(closingStockRaw);
    const params = wizardData?.parameters || {};
    const avgUpt = metrics.hasApiUpt
      ? toNumber(metrics.avgUpt)
      : calcAvgUptFromWeeks(metrics.qty, [params.week1Gc, params.week2Gc, params.week3Gc]);
    const overallProjectedGc = toNumber(params.projectedGc);
    const projectedGcForBuffer =
      overallProjectedGc > 0
        ? overallProjectedGc
        : calcProjectedGc(average([params.week1Gc, params.week2Gc, params.week3Gc]), growth);
    const gcBuffer = calcGcBuffer(projectedGcForBuffer, buffer);
    const useHistory = projected.hasData || metrics.fromApi;
    const calculatedBuildTo =
      useHistory ? calcBuildTo(avgUpt, gcBuffer) : null;
    const buildTo = useHistory
      ? calculatedBuildTo
      : firstFinite(skuRec.buildTo, item.buildTo, item.totalNeed);
    const suggestedOrder = useHistory
      ? buildTo == null
        ? null
        : calcSuggestedOrder(buildTo, closingStockQty)
      : firstFinite(skuRec.suggestedOrder, item.suggestedOrder, item.totalRequirement);
    const finalOrder = useHistory
      ? suggestedOrder == null
        ? null
        : calcFinalOrder(suggestedOrder)
      : firstFinite(skuRec.finalOrder, item.finalOrder);
    const sps1 = productSpsValue(item, 1);
    const sps2 = productSpsValue(item, 2);
    const sps3 = productSpsValue(item, 3);
    const finalOrderByOutlet = finalOrderByOutletValue(item, wizardData, finalOrder);
    const outletOrderForSps =
      finalOrderByOutlet === '' ? finalOrder : toNumber(finalOrderByOutlet);
    const calculatedSps = calcSpsOrders(outletOrderForSps, sps1, sps2, sps3);
    const spsOrds = useHistory
      ? calculatedSps
      : {
          spsOrd1: firstFinite(skuRec.spsOrd1, item.spsOrd1, calculatedSps.spsOrd1),
          spsOrd2: firstFinite(skuRec.spsOrd2, item.spsOrd2, calculatedSps.spsOrd2),
          spsOrd3: firstFinite(skuRec.spsOrd3, item.spsOrd3, calculatedSps.spsOrd3),
        };
    const projectedSales = useHistory
      ? projected.projectedSales == null
        ? null
        : roundTo(projected.projectedSales, 0)
      : firstFinite(skuRec.projectedSales, item.projectSale, item.projectedSales);
    const projectedGc = useHistory
      ? projected.projectedGc == null
        ? null
        : roundTo(projected.projectedGc, 0)
      : firstFinite(skuRec.projectedGc, item.projectGc, item.projectedGc);
    return {
      item,
      projectedSales: projectedSales == null ? null : roundTo(projectedSales, 0),
      projectedGc: projectedGc == null ? null : roundTo(projectedGc, 0),
      hasHistory: useHistory || projectedSales != null || buildTo != null,
      buildTo: buildTo == null ? null : roundTo(buildTo, 1),
      suggestedOrder: suggestedOrder == null ? null : roundTo(suggestedOrder, 1),
      finalOrder,
      finalOrderByOutlet,
      closingStock: closingStockRaw,
      sps1,
      sps2,
      sps3,
      spsOrd1: spsOrds.spsOrd1,
      spsOrd2: spsOrds.spsOrd2,
      spsOrd3: spsOrds.spsOrd3,
    };
  });
}
